import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Flame,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { cn } from '@/lib/utils'

import type { Sentiment } from '../../posts/model/contracts'
import type { TrendTopic } from '../model/contracts'
import { useLatestTrend } from '../state/useLatestTrend'

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

const SUMMARY_TRUNCATE_THRESHOLD = 120

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - Date.parse(iso)
  if (!Number.isFinite(diff)) return iso

  const seconds = Math.round(diff / 1000)
  if (seconds < 60) return rtf.format(-seconds, 'second')
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return rtf.format(-minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (hours < 24) return rtf.format(-hours, 'hour')
  return rtf.format(-Math.round(hours / 24), 'day')
}

const SENTIMENT_ICON: Record<Sentiment, { icon: typeof TrendingUp; className: string }> =
  {
    positive: {
      icon: TrendingUp,
      className: 'text-sentiment-positive',
    },
    negative: {
      icon: TrendingDown,
      className: 'text-sentiment-negative',
    },
    neutral: {
      icon: Minus,
      className: 'text-muted-foreground',
    },
  }

const SENTIMENT_BADGE: Record<Sentiment, string> = {
  positive:
    'border-sentiment-positive-border bg-sentiment-positive-surface text-sentiment-positive',
  negative:
    'border-sentiment-negative-border bg-sentiment-negative-surface text-sentiment-negative',
  neutral: 'border-border bg-secondary/60 text-muted-foreground',
}

function TrendTopicCard({ topic, index }: { topic: TrendTopic; index: number }) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const isLong = topic.summary.length > SUMMARY_TRUNCATE_THRESHOLD
  const sentimentIcon = SENTIMENT_ICON[topic.sentiment]
  const Icon = sentimentIcon.icon

  return (
    <div
      className="group relative flex gap-3 rounded-xl border border-border bg-card p-3 transition-all duration-200 hover:border-primary/20 hover:shadow-sm sm:p-4"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          topic.sentiment === 'positive'
            ? 'bg-sentiment-positive-surface'
            : topic.sentiment === 'negative'
              ? 'bg-sentiment-negative-surface'
              : 'bg-secondary',
        )}
      >
        <Icon size={16} strokeWidth={2.5} className={sentimentIcon.className} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-foreground sm:text-[15px]">
            {topic.title}
          </h3>
          <span
            className={cn(
              'inline-flex shrink-0 items-center rounded-full border px-1.5 py-px text-[10px] font-medium',
              SENTIMENT_BADGE[topic.sentiment],
            )}
          >
            {t(`trends.sentiment.${topic.sentiment}`)}
          </span>
        </div>
        <p
          className={cn(
            'text-xs leading-relaxed text-muted-foreground sm:text-sm',
            !isExpanded && isLong && 'line-clamp-2',
          )}
        >
          {topic.summary}
        </p>
        {isLong && (
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="mt-1 flex items-center gap-0.5 text-xs font-medium text-primary transition-colors hover:text-primary/80"
          >
            {isExpanded ? t('trends.showLess') : t('trends.readMore')}
            {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>
    </div>
  )
}

function TrendSkeleton() {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
      <div className="mb-4 flex items-center gap-2">
        <div className="h-5 w-5 animate-pulse rounded bg-secondary" />
        <div className="h-5 w-40 animate-pulse rounded bg-secondary" />
      </div>
      <div className="flex flex-col gap-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex gap-3 rounded-xl border border-border p-3 sm:p-4">
            <div className="h-8 w-8 shrink-0 animate-pulse rounded-lg bg-secondary" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 animate-pulse rounded bg-secondary" />
              <div className="h-3 w-full animate-pulse rounded bg-secondary" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const TrendDashboard = memo(function TrendDashboard() {
  const { t } = useTranslation()
  const { data: report, isLoading, error } = useLatestTrend()

  if (isLoading) return <TrendSkeleton />
  if (error || !report || report.topics.length === 0) return null

  return (
    <section
      aria-label={t('trends.title')}
      className="rounded-2xl border border-border bg-card p-4 shadow-sm dark:shadow-none sm:p-5"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame size={18} className="text-trend-hot" />
          <h2 className="text-sm font-bold text-foreground sm:text-base">
            {t('trends.title')}
          </h2>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <Clock size={12} />
          <span>{formatRelativeTime(report.createdAt)}</span>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:gap-3">
        {report.topics.map((topic, i) => (
          <TrendTopicCard key={topic.title} topic={topic} index={i} />
        ))}
      </div>

      {report.sourcePostIds.length > 0 && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          {t('trends.basedOn', { count: report.sourcePostIds.length })}
        </p>
      )}
    </section>
  )
})
